import {
  NgUploaderService,
  UploadFile,
  UploadInput,
  UploadOutput,
  UploaderOptions,
} from '@angular-ex/uploader';
import { CdkStep, StepperSelectionEvent } from '@angular/cdk/stepper';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef as MatDialogReference,
} from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { GridOptions, IRowNode } from 'ag-grid-community';
import { NGXLogger } from 'ngx-logger';
import { Observable, interval } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  BulkCSVUpdateJobStatus,
  BulkCSVUpdateMapping,
  BulkCSVUpdateMappings,
  FieldDataType,
  Fields,
  ListType,
  UserFriendlyError,
} from 'models';
import { AppConfigQuery } from 'src/app/modules/app-config';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { NotificationService } from 'src/app/services/notification.service';
import { ArchivesQuery } from 'src/app/state/archives/archives.query';
import { DatabasesQuery } from 'src/app/state/databases/databases.query';
import { FieldsQuery } from 'src/app/state/fields/fields.query';
import { GridSettingsService } from 'src/app/state/grid/grid-states.service';

import { AgGridAngular } from 'ag-grid-angular';
import { assertExists } from 'common';
import { BulkCSVUpdateService } from 'src/app/services/bulk-csv-update-dialog.service';
import {
  SelectionCellRendererComponent,
  SelectionCellRendererParameters,
} from '../selection-cell-renderer/selection-cell-renderer.component';

/** Enum for tracking status. */
export enum Status {
  /** Cancel. */
  cancel,
  /** Complete. */
  complete,
  /** Waiting for confirmation. */
  confirm,
  /** Updating. */
  updating,
}
/** CSV Update Dialog Data. */
export interface BulkCSVUpdateDialogData {
  /** Target Database ID. */
  databaseId: number;
}

/** CSV update dialog result. */
export interface BulkCSVUpdateDialogResult {
  /** Job ID for the started update job. */
  jobId: string;
}

/** Describes the csv mapping form. */
interface CsvMappingForm {
  /** Whether to allow system fields to be overwritten. */
  overwriteSystemFields: FormControl<boolean>;
}
/**
 * CSV Upload response.
 */
declare type CSVUploadResult = { [csvFilename: string]: string };
/** CSV update Dialog. */
@Component({
  selector: 'app-bulkcsvupdate-dialog',
  templateUrl: './bulk-csv-update-dialog.component.html',
  styleUrls: ['./bulk-csv-update-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BulkCSVUpdateDialogComponent implements OnInit {
  /** AG Grid Instance. */
  @ViewChild('grid')
  grid: AgGridAngular;
  /** Reference to the csv file input element. */
  @ViewChild('jobFileUploader')
  jobFileUploader: ElementRef;
  /** Mat stepper. */
  @ViewChild('stepper')
  stepper: MatStepper;
  /** Current archive. */
  archive$ = this.archivesQuery.getActive();
  /** Whether the archive id has been mapped by the user. */
  archiveIdMapped = false;
  /** Whether the document id has been mapped by the user. */
  documentIdMapped = false;
  /** CSV file upload form group. */
  csvFileUploadFormGroup: UntypedFormGroup;
  /** CSV field mapping form group. */
  csvFieldMappingFormGroup: FormGroup<CsvMappingForm>;
  /** Upload emitter for sending commands to file uploader. */
  csvUploadInput = new EventEmitter<UploadInput>();
  /** CSV upload progress. */
  csvUploadProgress = 0;
  /** Current document being updated. */
  currentDocument = 0;
  /** Database Id. */
  databaseId = this.databaseQuery.activeId;
  /** Errored rows. */
  erroredRows = 0;
  /** Archive fields. */
  fields: Fields = [];
  /** Grid options. */
  gridOptions: GridOptions;
  /** CSV job column mappings. */
  jobColumnMappings: Observable<BulkCSVUpdateMappings>;
  /** Array of job files being uploaded. */
  jobFiles: UploadFile[] = [];
  /** Job file upload input. */
  jobUploadInput = new EventEmitter<UploadInput>();
  /** Progress. */
  progress = 0;
  /** Whether to show the next step button. */
  showGoToNextStep = true;
  /** Whether to show the previous step button. */
  showGoToPreviousStep = false;
  /** Track update status. */
  status = Status.confirm;
  /** Status enums. */
  statusEnums = Status;
  /** Number of documents to be updated. */
  totalDocuments = 0;
  /** Uploader options bound to the @angular-ex/uploader. */
  uploaderOptions$: Observable<UploaderOptions> =
    this.appConfigQuery.concurrentUploads$.pipe(
      map((concurrentUploads) => ({
        concurrency: concurrentUploads,
      }))
    );
  private currentStep: CdkStep;
  private jobId: string;
  private uploadService: NgUploaderService;

  /**
   * Whether the field mapper has at least one included field.
   *
   * @returns True if at least one field is included.
   */
  get atLeastOneFieldIncluded(): boolean {
    let atLeastOneFieldIncluded = false;
    this.grid.api.forEachNode((rowNode) => {
      if (rowNode.data.includeField) {
        atLeastOneFieldIncluded = true;
        return;
      }
    });

    return atLeastOneFieldIncluded;
  }

  /**
   * Whether the CSV upload step is completed.
   *
   * @return True if the CSV upload step is completed.
   */
  get csvUploadStepCompleted(): boolean {
    // The step is complete if the form is valid and there is a job ID. However, Angular doesn't mark disabled form groups as valid.
    // In this case the form group is disabled because we have already submitted the form to the server in creating a job so it is valid.
    // We also confirm tha tth ejob is successfully created by checking the job ID which should have been set on upload success.
    return (
      (this.csvFileUploadFormGroup.valid ||
        this.csvFileUploadFormGroup.disabled) &&
      !!this.jobId
    );
  }

  /**
   * Whether the current step is invalid.
   *
   * @returns True if the current step is invalid. False otherwise.
   */
  get currentStepInvalid(): boolean {
    if (!this.currentStep)
      return this.csvFileUploadFormGroup.invalid || !this.jobId;
    return this.currentStep.hasError || !this.jobId;
  }

  /**
   * Determines if the step is editable.
   *
   * @returns True if the step is editable.
   */
  get stepIsEditable(): boolean {
    // The user can't edit anything at any point after starting an update so the step shouldn't be editable.
    return this.status === Status.confirm;
  }
  /**
   * Determines if the next button should be disabled.
   *
   * @returns True if the update button should be disabled. Otherwise false.
   */
  get disableUpdate(): boolean {
    return !this.gridDataValid();
  }

  /**
   * Whether the start update button should be disabled.
   *
   * @returns True if the start update button should be disabled.
   */
  get disableStartUpdate(): boolean {
    return (
      this.csvFileUploadFormGroup.invalid ||
      this.csvFieldMappingFormGroup.invalid ||
      !this.gridDataValid()
    );
  }

  constructor(
    private logger: NGXLogger,
    private cdr: ChangeDetectorRef,
    private formBuilder: UntypedFormBuilder,
    private archivesQuery: ArchivesQuery,
    private databaseQuery: DatabasesQuery,
    private notify: NotificationService,
    private authenticationService: AuthenticationService,
    private fieldsQuery: FieldsQuery,
    private gridService: GridSettingsService,
    private dialogReference: MatDialogReference<BulkCSVUpdateDialogComponent>,
    private bulkCSVUpdateService: BulkCSVUpdateService,
    private appConfigQuery: AppConfigQuery,
    @Inject(MAT_DIALOG_DATA) public dialogData: BulkCSVUpdateDialogData
  ) {
    this.fields = this.fieldsQuery.getAll();
    this.fields.splice(
      0,
      0,
      {
        format: '',
        id: -1,
        length: 50,
        multiValue: false,
        name: 'ArchiveId',
        regex: '',
        required: false,
        systemField: '',
        type: FieldDataType.integer,
        useRegExValidation: false,
        list: {
          listId: 0,
          mapping: [],
          primary: 0,
          secondary: 0,
          type: ListType.typeahead,
        },
      },
      {
        format: '',
        id: -2,
        length: 50,
        multiValue: false,
        name: 'DocId',
        regex: '',
        required: false,
        systemField: '',
        type: FieldDataType.integer,
        useRegExValidation: false,
        list: {
          listId: 0,
          mapping: [],
          primary: 0,
          secondary: 0,
          type: ListType.typeahead,
        },
      }
    );
    this.uploadService = new NgUploaderService();
    this.uploadService.serviceEvents.subscribe((event: UploadOutput) =>
      this.onCSVUploadOutput(event)
    );
  }
  /**
   * Checks if the column mapping grid is currently valid.
   *
   * @returns Whether the grid data is currently valid.
   */
  gridDataValid(): boolean {
    if (!this.grid) return false; // grid data can't be valid if there is no grid yet.
    this.archiveIdMapped = false;
    this.documentIdMapped = false;
    let fieldIncludedWithoutMapping = false;
    this.grid.api.forEachNode((rowNode) => {
      if (rowNode.data.includeField) {
        if (rowNode.data.fieldId === -1) {
          this.archiveIdMapped = true;
        } else if (rowNode.data.fieldId === -2) {
          this.documentIdMapped = true;
        } else if (!rowNode.data.fieldId || rowNode.data.fieldId === 0) {
          fieldIncludedWithoutMapping = true;
          return;
        }
      }
    });
    return (
      this.archiveIdMapped &&
      this.documentIdMapped &&
      !fieldIncludedWithoutMapping
    );
  }
  ngOnInit(): void {
    this.csvFileUploadFormGroup = this.formBuilder.group({
      csvFile: ['', Validators.required],
      columnDelimiter: [',', Validators.required],
      stringDelimiter: ['"', Validators.required],
      containsColumnHeaders: [false],
    });
    this.csvFieldMappingFormGroup = this.formBuilder.group({
      overwriteSystemFields: new FormControl(false, { nonNullable: true }),
    });
    this.configureMappingGrid();
    this.uploadService.initInputEvents(this.csvUploadInput);
  }
  /**
   * Handler for the upload output event.
   *
   * @param outputEvent @angular-ex/uploader upload output event.
   */
  onCSVUploadOutput(outputEvent: UploadOutput): void {
    this.logger.debug('On upload event triggered', outputEvent);
    switch (outputEvent.type) {
      case 'allAddedToQueue':
        this.csvUploadProgress = 0;
        break;
      case 'rejected': {
        this.notify.error(
          'File upload was rejected. Ensure the file was a CSV.'
        );

        break;
      }
      case 'done': {
        this.logger.debug('Upload is done.', outputEvent);
        if (outputEvent.file?.responseStatus === 200) {
          // this.csvFileUploadFormGroup.controls.csvFile.setValue(output.file.name);
          this.onCsvUploadComplete(outputEvent.file.response);
        } else {
          this.notify.error({
            error: outputEvent.file?.response,
            description: `The ${outputEvent.file?.name} file could not be uploaded.`,
            i18n: 'ERROR_FILE_UPLOAD_FAILED',
          });
        }

        break;
      }
      case 'uploading': {
        this.csvUploadProgress =
          outputEvent.file?.progress.data?.percentage ?? 0;

        break;
      }
      // No default
    }
  }
  /** Cancels the job by job id. */
  onCancel() {
    this.bulkCSVUpdateService.cancelJob(this.jobId).subscribe();
  }
  /**
   * Handler for the download results button click event.
   *
   */
  onClickDownloadResults(): void {
    this.bulkCSVUpdateService.downloadJobResult(this.jobId).subscribe();
  }

  /**
   * Handler for the include all button click handler.
   *
   * @param includeAll Whether to include all.
   */
  onClickToggleIncludeAll(includeAll: boolean): void {
    // Loops through all
    this.grid.api.forEachNode((rowNode) => {
      rowNode.setDataValue('includeField', includeAll);
    });

    this.grid.api.refreshCells({ force: true });
  }

  /** Close dialog. */
  onClose() {
    if (!this.jobId) {
      this.dialogReference.close('Cancelled');
    } else {
      this.bulkCSVUpdateService.deleteJob(this.jobId).subscribe(() => {
        this.gridService.refreshArchiveGrid(
          this.databaseId,
          this.archive$?.id || 0
        );
        this.dialogReference.close('Complete');
      });
    }
  }
  /**
   * Handler for the reset event.
   */
  onReset(): void {
    if (this.jobId) {
      this.logger.debug('Delete job.', this.jobId);
      this.bulkCSVUpdateService.deleteJob(this.jobId).subscribe(() => {
        this.jobId = '';
      });
    }
    this.stepper.reset();
    this.csvFileUploadFormGroup.enable();
    this.csvFileUploadFormGroup.controls.csvFile.setValue('');
    this.csvFileUploadFormGroup.controls.columnDelimiter.setValue(',');
    this.csvFileUploadFormGroup.controls.stringDelimiter.setValue('"');
    this.csvFileUploadFormGroup.controls.containsColumnHeaders.setValue(false);

    this.csvFieldMappingFormGroup.controls.overwriteSystemFields.setValue(
      false
    );
    this.status = Status.confirm;
  }

  /**
   * Event handler for the select CSV button.
   */
  onStartUpload(): void {
    this.jobFiles = [];
    const file = this.csvFileUploadFormGroup.controls.csvFile.value as File;
    if (!file) return;
    const columnDelimiter =
      this.csvFileUploadFormGroup.controls.columnDelimiter.value;
    const stringDelimiter =
      this.csvFileUploadFormGroup.controls.stringDelimiter.value;
    const containsColumnHeaders = this.csvFileUploadFormGroup.controls
      .containsColumnHeaders.value as boolean;
    const csvJobRequest = this.bulkCSVUpdateService.getCreateJobRequest(
      this.dialogData.databaseId,
      columnDelimiter,
      stringDelimiter,
      containsColumnHeaders,
      this.authenticationService.user.token
    );
    const fileList = this.createrFileList([file]);
    this.uploadService.handleFiles(fileList);
    this.csvUploadInput.emit({
      type: 'uploadAll',
      url: csvJobRequest.url,
      method: csvJobRequest.method,
      headers: csvJobRequest.headers,
    });
  }

  /**
   * Event handler for the step selection change event.
   *
   * @param event Stepper selection event.
   */
  onStepSelectionChange(event: StepperSelectionEvent): void {
    this.logger.debug('Stepper step changed', event);
    this.currentStep = event.selectedStep;
    this.showGoToNextStep = event.selectedIndex < this.stepper.steps.length - 1;
    this.showGoToPreviousStep =
      event.selectedIndex === this.stepper.steps.length - 1;
  }

  /** Handler for the update documents event. */
  onUpdateDocuments(): void {
    const columnMappings: BulkCSVUpdateMappings = [];
    this.grid.api.forEachNode((rowNode) =>
      columnMappings.push(rowNode.data as BulkCSVUpdateMapping)
    );
    this.logger.debug('Update documents clicked.', columnMappings);

    const overwriteSystemFields = this.csvFieldMappingFormGroup.controls
      .overwriteSystemFields.value as boolean;

    this.bulkCSVUpdateService
      .startJob(this.jobId, columnMappings, overwriteSystemFields)
      .subscribe({
        next: (job) => {
          this.totalDocuments = job.status.totalRows;
          this.logger.debug('CSV update job started.', this.jobId);
          this.status = Status.updating;
          this.runProgressBar();
          // this.dialogReference.close(result);
        },
        error: (error: UserFriendlyError) => this.notify.error(error),
      });
  }

  /** Progress bar animation. */
  runProgressBar() {
    const sub = interval(5000).subscribe(() => {
      this.bulkCSVUpdateService.getJobStatus(this.jobId).subscribe((status) => {
        this.progress = status.progress;
        this.erroredRows = status.erroredRows;
        this.currentDocument = status.currentRow;
        this.updateStatus(status);
        this.cdr.detectChanges();
        if (this.status !== Status.updating) {
          // The job is no longer updating so we can stop this observable.
          this.logger.debug(
            `Job ${this.jobId} status is ${this.status}. Stopping job status refresh.`
          );
          sub.unsubscribe();
        }
      });
    });
  }

  /**
   * Update the status.
   *
   * @param status - Status response.
   */
  updateStatus(status: BulkCSVUpdateJobStatus): void {
    if (this.progress === 100) {
      this.status = Status.complete;
    } else if (status.cancelled) {
      this.status = Status.cancel;
    }
  }

  private configureMappingGrid(): void {
    this.gridOptions = {
      onGridReady: () => {
        this.listenForOverwriteSystemFields();
      },
      pagination: false,
      columnDefs: [
        {
          field: 'displayName',
          headerName: 'Display Name',
          sortable: false,
          filter: false,
          editable: false,
        },
        {
          field: 'includeField',
          headerName: 'Include Field',
          sortable: false,
          filter: false,
          editable: true,
          cellRenderer: 'agCheckboxCellRenderer',
          valueSetter: (params) => {
            const newValue = params.newValue;
            const data = params.data;

            // Update includeField
            data.includeField = newValue;

            // Clear fieldId if checkbox is unchecked
            if (!newValue) {
              data.fieldId = null;
            }
            assertExists(params.node);

            if (newValue) {
              // Check if any field has the same name as the display name.
              const fieldForColumn = this.fields.find(
                (f) =>
                  f.name.toLowerCase() === params.data.displayName.toLowerCase()
              );
              if (fieldForColumn) {
                this.logger.debug(
                  'Field with a name that matches the column was found. Automapping it.'
                );
                params.data.fieldId = fieldForColumn.id;
              }
            }

            // Force refresh of the entire row to re-evaluate disabled state
            params.api.refreshCells({
              rowNodes: [params.node],
              force: true,
            });

            // Return true to indicate the value was set
            return true;
          },
          cellRendererParams: {
            forceAllCellsToRefresh: true,
          },
        },
        {
          field: 'fieldId',
          headerName: 'Mapped Field',
          sortable: false,
          filter: false,
          editable: false,
          cellRenderer: SelectionCellRendererComponent,
          cellRendererParams: {
            disabled: (params: SelectionCellRendererParameters) => {
              return !params.node.data.includeField;
            },
            selectionOptions: () => {
              const fields = this.fields
                .filter(
                  (field) =>
                    (!field.systemField ||
                      this.csvFieldMappingFormGroup.controls
                        .overwriteSystemFields.value) &&
                    field.systemField !== 'documentVersion' &&
                    field.systemField !== 'documentVersionParent' &&
                    field.systemField !== 'lastModified' &&
                    !field.liveField
                )
                .sort((a, b) => {
                  if (a.name.toLowerCase() < b.name.toLowerCase()) {
                    return -1;
                  }
                  if (a.name.toLowerCase() > b.name.toLowerCase()) {
                    return 1;
                  }
                  return 0;
                })
                .map((field) => ({
                  value: field.id,
                  display: field.name,
                }));
              return fields;
            },
          },
        },
      ],
    };
  }

  /**.
   * Helper method for creating a file list object that @angular-ex/uploader can use.
   *
   * @param files Files
   * @returns A file list object.
   */
  private createrFileList(files: File[]): FileList {
    const fileList: any = {
      length: files.length,
      // eslint-disable-next-line jsdoc/require-jsdoc
      item: (index: number): File => fileList[index],
    };

    for (const [index, file] of files.entries()) fileList[index] = file;
    return fileList;
  }

  /**
   * Listens for changes to the overwrite system fields form control and updates the mapping grid.
   */
  private listenForOverwriteSystemFields(): void {
    this.csvFieldMappingFormGroup.controls.overwriteSystemFields.valueChanges.subscribe(
      (overwriteSystemFields) => {
        let rowsToChange: IRowNode<any>[] | undefined = undefined;
        if (!overwriteSystemFields) {
          // Overwrite system fields has been toggled off so look for any columns that have system fields selected
          rowsToChange = [];
          assertExists(rowsToChange);
          this.grid.api.forEachNode((node) => {
            if (node.data.fieldId === null) return; // This row is not mapped so ignore it.
            const field = this.fields.find(
              (field) => field.id === node.data.fieldId
            );
            if (!field || field.systemField) {
              node.data.fieldId = null;
              // The ! is used here because we know that rowsToChange is not undefined due to above initialization and assertion.
              rowsToChange!.push(node);
            }
            this.logger.debug('Row node: ', node);
          });

          // Force all field id columns to refresh for all rows that had system fields selected.
          this.grid.api.refreshCells({
            columns: ['fieldId'],
            rowNodes: rowsToChange,
            force: true,
          });
        }
        // Force the field id column to refresh for all rows so that the selection options are updated.
        this.grid.api.refreshCells({
          columns: ['fieldId'],
          force: true,
        });
      }
    );
  }

  private onCsvUploadComplete(csvResponse: CSVUploadResult): void {
    this.logger.debug('CSV Upload completed successfully', csvResponse);
    this.jobId = csvResponse[Object.keys(csvResponse)[0]];
    this.bulkCSVUpdateService
      .getJobColumnMappings(this.jobId)
      .subscribe((mappings) => {
        const rowData = mappings.map((columnMapping) => ({
          displayName: columnMapping.displayName,
          includeField: columnMapping.includeField,
          isFilepath: columnMapping.isFilepath,
          fieldId: columnMapping.fieldId,
          columnIndex: columnMapping.columnIndex,
        }));
        this.grid.api.setGridOption('rowData', rowData);
        this.grid.api.hideOverlay();
        this.csvFileUploadFormGroup.disable();
        this.stepper.next();
      });
  }
}
